import React, { useState, useEffect } from "react";
import {
  ShoppingBag,
  LocalShipping,
  Storefront,
  Star,
  ShoppingCart,
} from "@mui/icons-material";
// import favicon from "..//../assets/favicon-96x96.png";
import logoicon from "..//../assets/Tobikor2.png";

function AppLoading({ variant = "spinner", showProgress = false }) {
  const [progress, setProgress] = useState(0);
  const [dots, setDots] = useState("");
  const [pulseScale, setPulseScale] = useState(1);

  // Animated dots effect
  useEffect(() => {
    const dotInterval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
    }, 500);
    return () => clearInterval(dotInterval);
  }, []);

  // Progress bar animation
  useEffect(() => {
    if (showProgress) {
      const interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + Math.random() * 15;
        });
      }, 200);
      return () => clearInterval(interval);
    }
  }, [showProgress]);

  // Pulse animation
  useEffect(() => {
    if (variant === "pulse") {
      const interval = setInterval(() => {
        setPulseScale((prev) => (prev === 1 ? 1.1 : 1));
      }, 500);
      return () => clearInterval(interval);
    }
  }, [variant]);

  // Responsive loading variants
  const loadingVariants = {
    spinner: (
      <div className="relative">
        <div className="animate-spin rounded-full h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 lg:h-32 lg:w-32 border-4 border-purple-200 border-t-coffee"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <ShoppingBag className="text-coffee text-lg sm:text-xl md:text-2xl animate-pulse" />
        </div>
      </div>
    ),
    dots: (
      <div className="flex space-x-2">
        <div className="w-3 h-3 sm:w-4 sm:h-4 bg-coffee rounded-full animate-bounce"></div>
        <div
          className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-500 rounded-full animate-bounce"
          style={{ animationDelay: "0.1s" }}
        ></div>
        <div
          className="w-3 h-3 sm:w-4 sm:h-4 bg-purple-400 rounded-full animate-bounce"
          style={{ animationDelay: "0.2s" }}
        ></div>
      </div>
    ),
    pulse: (
      <div
        className="transition-transform duration-500"
        style={{ transform: `scale(${pulseScale})` }}
      >
        <div className="bg-gradient-to-r from-brown to-lightBrown  rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl">
          <Storefront className="text-white text-2xl sm:text-3xl md:text-4xl" />
        </div>
      </div>
    ),
    bars: (
      <div className="w-48 sm:w-56 md:w-64 space-y-2">
        <div className="h-1.5 sm:h-2 bg-purple-200 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-brown to-lightBrown  rounded-full animate-shimmer"></div>
        </div>
        <div className="h-1.5 sm:h-2 bg-purple-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brown to-lightBrown  rounded-full animate-shimmer"
            style={{ animationDelay: "0.2s" }}
          ></div>
        </div>
        <div className="h-1.5 sm:h-2 bg-purple-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brown to-lightBrown  rounded-full animate-shimmer"
            style={{ animationDelay: "0.4s" }}
          ></div>
        </div>
      </div>
    ),
    cart: (
      <div className="relative">
        <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 border-4 border-purple-300 rounded-full animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <ShoppingCart className="text-coffee text-xl sm:text-2xl md:text-3xl animate-bounce" />
        </div>
        <div className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 bg-red-500 rounded-full animate-ping"></div>
      </div>
    ),
    gradient: (
      <div className="relative">
        <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-32 lg:h-32 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 animate-spin-slow"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center">
            <LocalShipping className="text-coffee text-base sm:text-lg md:text-xl lg:text-2xl" />
          </div>
        </div>
      </div>
    ),
    modern: (
      <div className="space-y-3 sm:space-y-4">
        <div className="flex justify-center">
          <div className="relative">
            <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full border-4 border-transparent border-t-purple-500 border-r-pink-500 border-b-blue-500 border-l-green-500 animate-spin"></div>
            <Star className="absolute inset-0 m-auto text-yellow-500 text-lg sm:text-xl md:text-2xl animate-pulse" />
          </div>
        </div>
        <div className="text-center">
          <p className="text-gray-600 font-medium text-small sm:text-base">
            Preparing your experience{dots}
          </p>
        </div>
      </div>
    ),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-white flex flex-col items-center justify-center w-full p-4 sm:p-6 md:p-8">
      <div className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto">
        {/* Logo/Brand */}
        <div className="mb-6 sm:mb-8 text-center">
          <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4 ">
          <div className="px-6 py-1 bg-gradient-to-r from-coffeeBrown to-coffee rounded-lg">

            <img src={logoicon} alt="logo" className="w-18 h-auto sm:w-24 sm:h-auto" />
          </div>
          </div>
          <p className="text-gray-500 text-small sm:text-small">
            Loading your shopping experience
          </p>
        </div>

        {/* Main Loading Animation */}
        <div className="mb-8 sm:mb-12 flex justify-center">
          {loadingVariants[variant]}
        </div>

        {/* Progress Bar (if enabled) */}
        {showProgress && (
          <div className="w-full max-w-xs sm:max-w-sm md:max-w-md mx-auto mb-6 sm:mb-8">
            <div className="flex justify-between text-small sm:text-small text-gray-600 mb-2">
              <span>Loading</span>
              <span>{Math.min(progress, 100).toFixed(0)}%</span>
            </div>
            <div className="h-2 sm:h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-brown to-lightBrown rounded-full transition-all duration-300"
                style={{ width: `${Math.min(progress, 100)}%` }}
              ></div>
            </div>
            <div className="mt-2 text-small text-gray-500 text-center">
              {progress < 30 && "Initializing..."}
              {progress >= 30 && progress < 60 && "Loading products..."}
              {progress >= 60 && progress < 90 && "Almost there..."}
              {progress >= 90 && "Finalizing..."}
            </div>
          </div>
        )}

        {/* Loading Tips */}
        <div className="max-w-md mx-auto text-center space-y-3 sm:space-y-4">
          <div className="text-gray-500">
            <p className="mb-1 sm:mb-2 text-small sm:text-small">
              💡 Did you know?
            </p>
            <p className="italic text-small sm:text-small">
              {progress % 3 === 0 && "Free shipping on orders over GH₵ 100"}
              {progress % 3 === 1 &&
                "We offer 30-day return policy on all items"}
              {progress % 3 === 2 && "New deals added daily!"}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 sm:mt-12 text-center text-small text-gray-400">
          <p>© {new Date().getFullYear()} Tobikor. All rights reserved.</p>
        </div>
      </div>

      {/* Custom CSS for additional animations */}
      <style jsx>{`
        @keyframes spin-slow {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
}

export default AppLoading;
